import { FreeDataValue, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { WizConstants } from '../../model/constants';
import { FreeDataVO } from './freedata.vo';
import { Model } from '../../model/model';
import { WizMathUtils } from '@wizeoo/akm-core-lib';

/**
 * VO représentant une valeur de freedata
 * Réduit à son minimum pour utilisation par le moteur
 */
export class FreedataValueVO {

    constructor(model: Model, pSource?: FreeDataValue) {
        this._model = model; 
        this._source = null;
        if (pSource) {
            this._source = pSource;
        } else {
            this._source = new FreeDataValue();
            this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
        }
    }

    /** Le modèle pour l'accés au parentVO */
    protected _model: Model;

    /** L'objet source provenant du back */
    protected _source: FreeDataValue;

    public set source(src: FreeDataValue) {
        this._source = src;
        // nettoyage des collection de args
        this.resetArgVO();
    }
    public get source(): FreeDataValue {
        return this._source;
    }

    public set id(value: number) {
        this._source.id = value;
    }
    public get id(): number {
        return this._source.id;
    }

    public set code(code: string) {
        this._source.code = code;
    }
    public get code(): string {
        return this._source.code;
    }

    public set shortCode(shortCode: string) {
        this._source.shortCode = shortCode;
    }
    public get shortCode(): string {
        return this._source.shortCode;
    }

    public set freeDataCode(freeDataCode: string) {
        this._source.freeDataCode = freeDataCode;
    }
    public get freeDataCode(): string {
        return this._source.freeDataCode;
    }

    public set parentCode(parentCode: string) {
        this._source.parentCode = parentCode;
    }
    public get parentCode(): string {
        return this._source.parentCode;
    }

    public set externalCode(code: string) {
        this._source.externalCode = code;
    }
    public get externalCode(): string {
        return this._source.externalCode;
    }

    public set externalParentCode(code: string) {
        this._source.externalParentCode = code;
    }
    public get externalParentCode(): string {
        return this._source.externalParentCode;
    }

    public set label(label: TranslatedEmb) {
        this._source.label = label;
    }
    public get label(): TranslatedEmb {
        return this._source.label;
    }

    public set description(description: TranslatedEmb) {
        this._source.description = description;
    }
    public get description(): TranslatedEmb {
        return this._source.description;
    }

    public set valor(valor: string) {
        this._source.valor = valor;
    }
    public get valor(): string {
        return this._source.valor;
    }

    public get icon(): string {
        return this._source.icon;
    }
    public set icon(pValue: string) {
        this._source.icon = pValue;
    }

    public set createBy(createBy: string) {
        this._source.createBy = createBy;
    }
    public get createBy(): string {
        return this._source.createBy;
    }

    public set updateBy(updateBy: string) {
        this._source.updateBy = updateBy;
    }
    public get updateBy(): string {
        return this._source.updateBy;
    }

    public set moreArgsJson(moreArgs: any) {
        this._source.moreArgsJson = moreArgs;
    }
    public get moreArgsJson(): any {
        return this._source.moreArgsJson;
    }

    public set createTime(createTime: number) {
        this._source.createTime = createTime;
    }
    public get createTime(): number {
        return this._source.createTime;
    }

    public set updateTime(updateTime: number) {
        this._source.updateTime = updateTime;
    }
    public get updateTime(): number {
        return this._source.updateTime;
    }

    public set startDate(startDate: number | null) {
        this.source.startDate = startDate;
    }
    public get startDate(): number | null {
        return this.source.startDate;
    }

    public set endDate(endDate: number | null) {
        this.source.endDate = endDate;
    }
    public get endDate(): number | null {
        return this.source.endDate;
    }

    /** Deprecié */
    public set deprecated(value: boolean) {
        this.source.deprecated = value;
    }
    public get deprecated(): boolean {
        return this.source.deprecated;
    }

    /** restrict */
    public set restrict(code: string) {
        this.source.restrict = code;
    }
    public get restrict(): string {
        return this.source.restrict;
    }

    public set level(level: number | null) {
        this.source.level = level;
    }
    public get level(): number | null {
        return this.source.level;
    }

    /**  */
    public set order(order: number | null) {
        this.source.order = order;
    }

    public get order(): number | null {
        return this.source.order;
    }

    /**
     * Concaténation freedataCode + code pour les map de freedatavalues
     */
    public get keyCode(): string {
        return this.freeDataCode + WizConstants.KEY_SEPARATOR + this.code;
    }

    /** FDV parent */
    private _parentVO: FreedataValueVO;

    public getParentVO(): FreedataValueVO {
        if (!this._parentVO) {
            this._parentVO = this._model.freedataEngine.getFreedataValue(this.freeDataCode, this.parentCode);
        }
        return this._parentVO;
    }

    public get parentShortCode(): string {
        let result: string = null;
        const parent: FreedataValueVO = this.getParentVO();
        if (!!parent) {
            result = parent.shortCode;
        }
        return result;
    }

    private _parentFDVO: FreeDataVO = null;

    public getParentFDVO(): FreeDataVO {
        if (!this._parentFDVO) {
            this._parentFDVO = this._model.freedataEngine.getFreeData(this.freeDataCode);
        }
        return this._parentFDVO;
    }

    /*
     * Return translated label
     * @return string
     */
    get trsLabel(): string {
        return this._model.localeEngine.translate(this.label, this.shortCode).trim();
    }

    /**
     * Return translated label
     * @return string
     */
    get trsDescription(): string {
        return this._model.localeEngine.translate(this.description);
    }

    /* ################################################
     *       gestion des champs complémentaires
     * ################################################ */

    /** manipulation des champs complémentaires, couples code: value */

    /** reset sur les champs args */
    public resetArgVO(): void {
        this._argsObjVO = null;
    }

    // mémoriser la collection de args
    private _argsObjVO: { [key: string]: string } = null;
    // langue utilisée pour la mémorisation
    private _argsLanguage: string = null;

    public get argsObjVO(): { [key: string]: string } {
        let pairs = {};
        if (!this._argsObjVO || this._model.currentLocaleCode !== this._argsLanguage) {
            pairs = {...this.trueArgsObj};
            this._model.freedataEngine.addBasicPairs(pairs, this);
            this._argsObjVO = pairs;
            this._argsLanguage = this._model.currentLocaleCode; // mémorisation de la langue
        }
        return this._argsObjVO;
    }

    public get trueArgsObj(): any {
        if (!!this.moreArgsJson) {
            return this.moreArgsJson;
        }
        return {};
    }

    public set trueArgsObj(value: any) {
        this.moreArgsJson = value; // JSON.stringify(value);
        this._argsObjVO = null; // reset de la collection d'objets
    }

}
